// frontend/src/auth/AuthBackdrop.jsx
/**
 * AuthBackdrop — static, no-data faux app shell shown behind the login overlay.
 *
 * @description
 *   Renders a visual scaffold of the app (sidebar shape + header bar + content
 *   skeletons) so the unauthenticated user sees "an app exists, you just need
 *   to log in." Replaces the prior blank-background full-screen Login. NO API
 *   calls fire from this component — it's pure MUI primitives + hardcoded
 *   text. The 2026-04-30 security context (no chrome for unauthed users)
 *   is preserved by keeping all real components (SidebarAndHeaderBar, Bell,
 *   NotificationsProvider) outside the unauthenticated branch.
 *
 * @useWhen
 *   - Building an authentication overlay screen
 *   - Need a faux app shell that conveys structure without firing data calls
 *
 * @dontUseFor
 *   - Real loading skeletons inside an authenticated route — use the actual
 *     page-level Skeleton components in src/Skeletons/ for that.
 *
 * @reuseHints
 *   - Could backstop a maintenance-mode page (read-only, no live API)
 */
import React from 'react';
import { Box, Skeleton, useTheme, useMediaQuery } from '@mui/material';

const SidebarRow = ({ width = '70%' }) => (
  <Skeleton
    variant="rounded"
    width={width}
    height={28}
    sx={{ mx: 2, my: 1, borderRadius: 1 }}
  />
);

const KpiCard = () => (
  <Box
    sx={{
      flex: 1,
      minWidth: 160,
      bgcolor: 'background.paper',
      borderRadius: 3,
      p: 2.5,
      boxShadow: 1,
    }}
  >
    <Skeleton variant="text" width="60%" height={14} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="40%" height={28} />
  </Box>
);

const TableRow = () => (
  <Box
    sx={{
      display: 'flex',
      gap: 2,
      py: 1.25,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Skeleton variant="text" width="20%" />
    <Skeleton variant="text" width="35%" />
    <Skeleton variant="text" width="15%" />
    <Skeleton variant="text" width="20%" />
  </Box>
);

export default function AuthBackdrop() {
  const theme = useTheme();
  const isWide = useMediaQuery(theme.breakpoints.up('md'));

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        bgcolor: 'background.default',
        overflow: 'hidden',
        // Blur + dim — login dialog sits on top in sharp focus.
        filter: 'blur(6px)',
        opacity: 0.65,
        pointerEvents: 'none',
        userSelect: 'none',
      }}
    >
      {/* Sidebar */}
      {isWide && (
        <Box
          sx={{
            width: 260,
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
            py: 2,
          }}
        >
          <Box sx={{ px: 2, mb: 3 }}>
            <Skeleton variant="rounded" width={120} height={22} />
          </Box>
          <SidebarRow width="80%" />
          <SidebarRow width="65%" />
          <SidebarRow width="75%" />
          <SidebarRow width="55%" />
          <SidebarRow width="70%" />
          <SidebarRow width="60%" />
          <SidebarRow width="68%" />
        </Box>
      )}

      {/* Main area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <Box
          sx={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Skeleton variant="text" width={140} height={20} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* KPI row */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <KpiCard />
            <KpiCard />
            <KpiCard />
            {isWide && <KpiCard />}
          </Box>

          {/* Table block */}
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              p: 2.5,
              boxShadow: 1,
            }}
          >
            <Skeleton variant="text" width="25%" height={22} sx={{ mb: 1.5 }} />
            <TableRow />
            <TableRow />
            <TableRow />
            <TableRow />
            <TableRow />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
